import { useEffect, useRef, useState } from "react";

const stats = [
  { icon: "ri-windy-line", label: "PM2.5", desc: "Fine particles penetrate deep into lungs and bloodstream", color: "bg-red-100", iconColor: "text-red-600", border: "border-red-100" },
  { icon: "ri-focus-3-line", label: "0.0146μm", desc: "Airdog TPA captures particles 20× finer than HEPA", color: "bg-brand/10", iconColor: "text-brand", border: "border-brand/15" },
  { icon: "ri-shield-check-line", label: "Never Clogs", desc: "Unlike HEPA, TPA plates never lose suction during smoke events", color: "bg-emerald-100", iconColor: "text-emerald-600", border: "border-emerald-100" },
];

const protections = [
  { icon: "ri-fire-line", title: "Wildfire Smoke", desc: "Destroys fine PM2.5 smoke particles that HEPA filters miss.", color: "bg-amber-100", iconColor: "text-amber-600", border: "border-amber-100" },
  { icon: "ri-drop-line", title: "Smoke Odors", desc: "Neutralizes the acrid smell of wildfire smoke, not just visible particles.", color: "bg-sky-100", iconColor: "text-sky-600", border: "border-sky-100" },
  { icon: "ri-radar-line", title: "Auto Response", desc: "Smart sensor detects smoke spikes and ramps up instantly — no manual adjustments.", color: "bg-rose-100", iconColor: "text-rose-600", border: "border-rose-100" },
  { icon: "ri-time-line", title: "24/7 Protection", desc: "Designed for continuous operation during extended wildfire seasons.", color: "bg-violet-100", iconColor: "text-violet-600", border: "border-violet-100" },
];

export default function WildfireSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="wildfire" className="relative scroll-mt-20" ref={ref}>
      {/* Banner */}
      <div className="w-full h-[110px] relative">
        <img
          src="https://storage.readdy-site.link/project_files/0ab4d894-9a87-4492-8cdb-8328f902642e/73ba447b-9d4e-4b46-87a8-b2ccf3c0bbbb_wildfire-smoke.png?v=d649368f181a3c26705ed97637e1ecd7"
          alt="Wildfire smoke"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/60" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center px-4">
            <span className="inline-block text-xs font-black tracking-[0.2em] uppercase text-white/80 mb-1">Wildfire Season</span>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white leading-tight">
              When Wildfires Burn, Your Indoor Air Turns <span className="text-red-500">Toxic</span>
            </h2>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="bg-[#FAF8F5] py-4 md:py-5">
        <div className="mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">

          {/* Intro */}
          <div className="max-w-4xl mx-auto mb-3 text-center">
            <p className="text-base md:text-lg text-gray-700 font-bold leading-relaxed">
              Wildfire smoke doesn&apos;t stay outside. PM2.5 particles — 30 times smaller than a human hair — infiltrate every room, triggering asthma, respiratory distress, and long-term health damage. Standard HEPA purifiers clog fast and lose efficiency when you need them most.
            </p>
          </div>

          {/* Heading */}
          <div className="mb-2 text-center">
            <span className="inline-block text-xs font-black tracking-[0.2em] uppercase text-brand mb-1">Airdog Protection</span>
            <h3 className="text-xl md:text-2xl font-bold text-gray-900 leading-tight">Built for Wildfire Season</h3>
          </div>

          {/* Stats cards */}
          <div className="grid sm:grid-cols-3 gap-2 mb-2">
            {stats.map((s, i) => (
              <div key={i} className={`bg-white rounded-lg p-2 border ${s.border} hover:border-brand/20 transition-all duration-500 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{ transitionDelay: `${i * 80}ms` }}>
                <div className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-md ${s.color} flex items-center justify-center shrink-0`}>
                    <i className={`${s.icon} ${s.iconColor} text-base`} />
                  </div>
                  <div>
                    <h3 className="text-lg font-black text-gray-900 leading-none">{s.label}</h3>
                    <p className="text-xs text-gray-700 font-bold leading-tight mt-0.5">{s.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Protection feature cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 pb-2">
            {protections.map((p, i) => (
              <div key={i} className={`bg-white rounded-lg p-3 border ${p.border} hover:border-brand/20 transition-all duration-500 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{ transitionDelay: `${350 + i * 80}ms` }}>
                <div className={`w-8 h-8 rounded-md ${p.color} flex items-center justify-center mb-1.5`}>
                  <i className={`${p.icon} ${p.iconColor} text-base`} />
                </div>
                <h4 className="text-sm font-black text-gray-900 mb-1 leading-tight">{p.title}</h4>
                <p className="text-xs text-gray-700 font-bold leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>

        </div>
      </div>
    </section>
  );
}